// src/pages/VerifyEmail.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { applyActionCode, checkActionCode } from 'firebase/auth';
import { auth } from '../services/firebase';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      const actionCode = searchParams.get('oobCode');
      
      if (!actionCode) {
        setStatus('error');
        setMessage('Invalid verification link');
        return;
      }

      try {
        // Verify the action code is valid
        await checkActionCode(auth, actionCode);
        
        // Apply the email verification
        await applyActionCode(auth, actionCode);
        
        setStatus('success');
        setMessage('Email verified successfully! You can now close this tab and return to the registration.');
        
        // Redirect after 3 seconds
        setTimeout(() => {
          navigate('/register'); // Adjust path as needed
        }, 3000);
        
      } catch (error) {
        console.error('Error verifying email:', error);
        setStatus('error');
        
        switch (error.code) {
          case 'auth/expired-action-code':
            setMessage('Verification link has expired. Please request a new one.');
            break;
          case 'auth/invalid-action-code':
            setMessage('Invalid verification link. Please try again.');
            break;
          case 'auth/user-disabled':
            setMessage('This account has been disabled.');
            break;
          default:
            setMessage('Failed to verify email. Please try again.');
        }
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {status === 'verifying' && (
            <>
              <div className="flex justify-center mb-4">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500"></div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Verifying Email...</h2>
              <p className="mt-2 text-gray-600">Please wait while we verify your email address.</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <h2 className="text-2xl font-bold text-green-900">Email Verified!</h2>
              <p className="mt-2 text-gray-600">{message}</p>
            </>
          )}
          
          {status === 'error' && (
            <>
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              </div>
              <h2 className="text-2xl font-bold text-red-900">Verification Failed</h2>
              <p className="mt-2 text-gray-600">{message}</p>
              <button 
                onClick={() => navigate('/register')}
                className="mt-4 bg-orange-500 text-white px-4 py-2 rounded-md hover:bg-orange-600"
              >
                Return to Registration
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;